// Browser-side API client.
//
// EVERY REQUEST CARRIES A BEARER TOKEN, and it is one of three things: the
// deployment secret, a workspace key, or a person's session. The server tells
// them apart in `resolveTenant`; here they are all just a string.
//
// WHERE THE TOKEN LIVES DEPENDS ON WHAT IT IS.
//
//   · A SESSION (`ses_…`) goes in localStorage. It expires, it is scoped to one
//     user, and signing out deletes it server-side — so persisting it is safe,
//     and NOT persisting it would mean a 30-day session that dies whenever a
//     tab closes, which is not a 30-day session.
//
//   · A WORKSPACE KEY or the deployment secret goes in sessionStorage, as
//     before. Those do not expire and cannot be revoked individually; keeping
//     one on disk turns a borrowed laptop into a permanent grant.
//
// The distinction is the point. It used to say "Netlify Identity replaces this
// before a second user exists" — that day arrived and the answer was passwords
// and a sessions table instead. See src/lib/auth-core.ts.

const KEY = 'hamer-crm-secret';

const isSession = (v: string) => v.startsWith('ses_');

export const getSecret = () =>
  localStorage.getItem(KEY) ?? sessionStorage.getItem(KEY);

export const setSecret = (v: string) => {
  const t = v.trim();
  // Never leave a copy in the other store — a stale key there would win or
  // linger depending on read order, which is the kind of bug that presents as
  // "it logged me back in as somebody else".
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY);
  (isSession(t) ? localStorage : sessionStorage).setItem(KEY, t);
};

export const clearSecret = () => {
  localStorage.removeItem(KEY);
  sessionStorage.removeItem(KEY);
};

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
  }
}

/**
 * GETs retry; writes never do.
 *
 * The server half of this app learned the flaky-link lesson long ago —
 * resilient() in store.ts, four attempts in the smoke script — and the
 * browser half never did: every request was one shot, so a single transient
 * failure during boot silently swapped the whole app onto demo fixtures and
 * every button "stopped firing". A read is safe to repeat; a write repeated
 * on a timeout is how the same email sends twice, so writes still surface
 * their first failure untouched.
 */
const RETRYABLE = new Set([502, 503, 504]);

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const method = (init.method ?? 'GET').toUpperCase();
  const attempts = method === 'GET' ? 3 : 1;

  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    if (i > 0) await new Promise((r) => setTimeout(r, 700 * i));
    const secret = getSecret();
    let res: Response;
    try {
      res = await fetch(path, {
        ...init,
        headers: {
          ...(init.headers ?? {}),
          'content-type': 'application/json',
          ...(secret ? { authorization: `Bearer ${secret}` } : {}),
        },
      });
    } catch (err) {
      lastErr = new ApiError(0, 'The server could not be reached.');
      continue;                       // network-level failure: retry a GET
    }
    if (RETRYABLE.has(res.status) && i < attempts - 1) {
      lastErr = new ApiError(res.status, res.statusText);
      continue;
    }
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      if (res.status === 401) clearSecret();
      throw new ApiError(res.status, (body as { error?: string }).error ?? res.statusText);
    }
    return body as T;
  }
  throw lastErr instanceof ApiError ? lastErr : new ApiError(0, String(lastErr));
}

// --- types -----------------------------------------------------------------

import type { Fit } from './lib/fit-core.js';

export interface CompanyCard {
  /** Computed against the default ICP on every read — see lib/fit-core.ts. */
  fit?: Fit | null;
  id: string;
  slug: string;
  name: string;
  stage: string;
  priority: string | null;
  fit_score: number | null;
  fit_tier: string | null;
  timing: string | null;
  gap_statement: string | null;
  next_action: string | null;
  next_action_date?: string | null;
  conflict_gate: string;
  /** No URL means the account cannot be enriched or searched — the most common stall. */
  url: string | null;
  /** Null means a firmographic credit has never been spent here. */
  enriched_at?: string | null;
  lifecycle: string;
  contact_count: number;
  contacts_with_email: number;
  open_question_count: number;
  sequence_status: string | null;
  touches_total: number;
  touches_sent: number;
  touches_written: number;
}

export interface Contact {
  id: string;
  name: string;
  title: string | null;
  role: string | null;
  entry_point: string | null;
  email: string | null;
  linkedin: string | null;
  source: string | null;
  notes: string | null;
  suppressed: boolean;
  apollo_person_id?: string | null;
  seniority?: string | null;
  email_status?: string;
}

export interface Touch {
  id: string;
  ref: string;
  day_offset: number;
  due_on: string | null;
  channel: string;
  act: number | null;
  subject: string | null;
  body: string | null;
  status: string;
  contact_name: string | null;
  notes: string | null;
}

export interface Activity {
  id: string;
  kind: string;
  occurred_at: string;
  summary: string;
  detail: string | null;
}

export interface Decision {
  id: string;
  decided_on: string;
  decision: string;
  decided_by: string;
  reasoning: string | null;
  rejected: string | null;
  status: string;
}

export interface OpenQuestion {
  id: string;
  question: string;
  blocking: boolean;
}

export interface CompanyDetail extends CompanyCard {
  url: string | null;
  hq: string | null;
  employees: number | null;
  founded: number | null;
  sub_category: string | null;
  description: string | null;
  revenue_band: string | null;
  funding_stage: string | null;
  total_raised: string | null;
  last_round_amount: string | null;
  last_round_lead: string | null;
  last_round_date: string | null;
  ownership_type: string | null;
  sponsor: string | null;
  motion: string | null;
  leadership_state: string | null;
  wedge: string | null;
  hamer_tier: string | null;
  source: string | null;
  intent_why: string | null;
  intent_win: string | null;
  intent_framing: string | null;
  intent_origin: string | null;
  enriched_at?: string | null;
  apollo_org_id?: string | null;
  competitors: string[];
  end_markets: string[];
  active_triggers: string[];
  hooks: string[];
  anecdotes: string[];
  investors: string[];
  contacts: Contact[];
  touches: Touch[];
  activities: Activity[];
  decisions: Decision[];
  open_questions: OpenQuestion[];
}

/** A contact with its company, for the people list view. */
export interface ContactRow extends Contact {
  company_slug: string;
  company_name: string;
  company_stage: string;
  touches_pending: number;
}

export interface Deal {
  id: string;
  company_id: string;
  company_slug: string;
  company_name: string;
  name: string;
  stage: string;
  tier: string | null;
  amount: number | null;
  expected_close: string | null;
  closed_at: string | null;
  outcome: string | null;
  notes: string | null;
}

export interface Task {
  id: string;
  company_id: string | null;
  company_slug: string | null;
  company_name: string | null;
  contact_name: string | null;
  title: string;
  due_on: string | null;
  done: boolean;
}

export interface Reply {
  id: string;
  company_slug: string;
  company_name: string;
  contact_name: string | null;
  touch_ref: string | null;
  received_at: string;
  classification: string | null;
  excerpt: string | null;
  body: string | null;
  action_taken: string | null;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  created_at: string;
}

export interface Connection {
  id: string; provider: string; label: string | null; status: string;
  secret_ref: string | null; meta: { note?: string };
  last_checked_at: string | null; last_error: string | null;
}

export interface SendingIdentity {
  id: string; domain: string; from_name: string | null; from_email: string;
  status: string; spf_ok: boolean | null; dkim_ok: boolean | null;
  dmarc_ok: boolean | null; daily_send_cap: number; is_default: boolean;
  /** Printed under this sender's cold mail. Falls back to the workspace's. */
  postal_address?: string | null;
}

export interface ProviderCredential {
  provider: string;
  /** Where the key resolves from. `env` wins and can't be edited in the UI. */
  source: 'env' | 'database' | null;
  configured: boolean;
  set_at: string | null;
  editable: boolean;
}

export interface Settings {
  org: { id: string; slug: string; name: string };
  connections: Connection[];
  identities: SendingIdentity[];
  members: { name: string | null; email: string; role: string }[];
  counts: { companies: number; contacts: number; sequences: number };
  providers?: ProviderCredential[];
}

/**
 * Store or clear one provider credential for this workspace.
 * Send an empty string to remove it. There is no matching GET — the API can
 * never hand a stored key back, so losing one means re-pasting it.
 */
export const setCredential = (provider: string, value: string) =>
  request<{ ok: true; provider: string; configured: boolean; masked: string | null }>(
    '/api/settings',
    { method: 'PUT', body: JSON.stringify({ provider, value }) },
  );

// --- calls ------------------------------------------------------------------

export const listCompanies = () =>
  request<{ ok: true; companies: CompanyCard[] }>('/api/companies');

export const getCompany = (slug: string) =>
  request<{ ok: true; company: CompanyDetail }>(`/api/companies/${slug}`);

export const getSettings = () =>
  request<{ ok: true; settings: Settings }>('/api/settings');

export const setStage = (slug: string, stage: string, note: string) =>
  request<{ ok: true }>(`/api/companies/${slug}`, {
    method: 'PATCH',
    body: JSON.stringify({ stage, note }),
  });

export const updateCompany = (slug: string, patch: Record<string, unknown>) =>
  request<{ ok: true }>(`/api/companies/${slug}`, {
    method: 'PATCH',
    body: JSON.stringify(patch),
  });

// Contacts are read through listRecords('contacts', …) below. The old
// listContacts() pointed at /api/contacts, which does not exist.

// --- enrichment (Apollo) ----------------------------------------------------
// Search is free. The other two cost one credit each — the UI must never call
// them in a loop or on render.

export interface SearchPerson {
  apollo_id: string;
  first_name: string;
  last_name_obfuscated: string;
  title: string | null;
  seniority: string | null;
  departments: string[];
  has_email: boolean;
  organization_name: string | null;
  linkedin_url: string | null;
}

export interface OrgEnrichment {
  industry: string | null;
  estimated_num_employees: number | null;
  founded_year: number | null;
  annual_revenue: number | null;
  total_funding: number | null;
  latest_funding_stage: string | null;
  latest_funding_date: string | null;
  technologies: string[];
  city: string | null;
  state: string | null;
}

/** 1 credit. */
export const enrichCompany = (slug: string, domain?: string) =>
  request<{ ok: true; enrichment: OrgEnrichment }>(`/api/enrich/company/${slug}`, {
    method: 'POST',
    body: JSON.stringify(domain ? { domain } : {}),
  });

/**
 * Free. Returns candidates only — nothing is written to the committee.
 *
 * `titles` and `page` are the whole point of the signature. The route has
 * always accepted them; this client passed only `domain`, so the search was
 * welded to the persona list with no page two and no way to ask for a title
 * the personas never mention. Kyle found that from the app.
 */
export const searchContacts = (slug: string, opts: {
  domain?: string; titles?: string[]; seniorities?: string[]; page?: number;
  /** Every senior person, whatever their title. Ignores `titles`. */
  broad?: boolean;
} = {}) =>
  request<{ ok: true; people: SearchPerson[]; total: number; page: number }>(
    `/api/enrich/search/${slug}`,
    { method: 'POST', body: JSON.stringify(opts) },
  );

/**
 * FREE. Put searched people on the account without buying their addresses.
 *
 * Makes no Apollo call — everything sent is data the search already returned.
 * Deduped on apollo_person_id server-side, so importing somebody twice is a
 * no-op rather than a twin.
 */
export const importContacts = (slug: string, people: SearchPerson[]) =>
  request<{ ok: true; imported: number; contacts: Contact[] }>(
    `/api/enrich/import/${slug}`,
    { method: 'POST', body: JSON.stringify({ people }) },
  );

/**
 * 1 credit. Enrich a contact already in the CRM — no Apollo id needed.
 * Matches on their name plus the account's domain.
 */
export const revealContactById = (slug: string, contactId: string) =>
  request<{ ok: true; contact: Contact; email_found: boolean }>(`/api/enrich/reveal/${slug}`, {
    method: 'POST',
    body: JSON.stringify({ contact_id: contactId }),
  });

/** 1 credit, for ONE person. Promotes them into the committee. */
export const revealContact = (slug: string, apolloId: string) =>
  request<{ ok: true; contact: Contact; email_found: boolean }>(`/api/enrich/reveal/${slug}`, {
    method: 'POST',
    body: JSON.stringify({ apollo_id: apolloId }),
  });

// --- records: the generic CRUD client ---------------------------------------
//
// This replaces listDeals / createDeal / listTasks / setTaskDone / listReplies /
// listContacts, which pointed at /api/deals, /api/tasks and friends. Those
// routes were never built — every one of those calls 404'd. Dead client code
// that looks alive is worse than none, because it reads as a working feature.
//
// One client against /api/records/:type, mirroring records.ts on the server.

export type RecordType =
  | 'companies' | 'contacts' | 'deals' | 'deal_contacts' | 'tasks' | 'activities'
  | 'emails' | 'icp_profiles' | 'personas' | 'news_items' | 'discovered_accounts'
  | 'open_questions' | 'decisions' | 'documents' | 'sending_identities'
  | 'sequences' | 'touches' | 'signals' | 'jobs' | 'offerings';

export interface Document {
  id: string; company_id: string | null; deal_id: string | null;
  contact_id: string | null; name: string; kind: string; url: string | null;
  notes: string | null; storage: 'link' | 'upload'; created_at: string;
}

/** Relationship warmth — how the room feels. Not the same axis as deal stage. */
export const RELATIONSHIPS = [
  'cold', 'aware', 'warm', 'engaged', 'trusted', 'client', 'past_client', 'do_not_contact',
] as const;

/** Contact engagement, derived from behaviour where possible. */
export const CONTACT_STATUS = [
  'new', 'contacted', 'opened', 'clicked', 'replied', 'meeting', 'bounced', 'unsubscribed',
] as const;

export const DOC_KINDS = [
  'proposal', 'contract', 'deck', 'one_pager', 'case_study', 'transcript',
  'research', 'invoice', 'other',
] as const;

type Filters = Record<string, string | number | boolean | null | undefined>;

export function listRecords<T = Record<string, unknown>>(type: RecordType, filters: Filters = {}) {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== null && v !== '') q.set(k, String(v));
  }
  const qs = q.toString();
  return request<{ ok: true; records: T[] }>(`/api/records/${type}${qs ? `?${qs}` : ''}`);
}

export interface CaptureResult {
  ok: true; kind: string;
  company: { name: string; domain: string | null; description?: string;
             existing: boolean; existing_id: string | null; existing_slug: string | null };
  contacts: { name: string; email?: string; title?: string;
              existing: boolean; existing_id: string | null }[];
  deal: { name: string; stage: string; amount?: number; next_step?: string } | null;
  activity: { summary: string; kind: string; occurred_at?: string; detail?: string };
  notes: string | null;
}

/** Read an email or invite into proposed records. Creates nothing. */
export const captureExtract = (text: string) =>
  request<CaptureResult>('/api/capture', { method: 'POST', body: JSON.stringify({ text }) });

/** Create the records the operator ticked. */
export const captureCommit = (payload: Record<string, unknown>) =>
  request<{ ok: true; company: { id: string; slug: string; name: string };
            counts: { contacts: number; deals: number; activities: number } }>(
    '/api/capture/commit', { method: 'POST', body: JSON.stringify(payload) });

export interface SweepSource {
  key: string; label: string; available: boolean;
  blocked_by?: string; fix?: string;
}
export interface SweepStatus {
  ok: true; sources: SweepSource[];
  last: { trigger: string; sources: string[]; scanned: number; found: number;
          created_at: string } | null;
}
export interface SweepResult {
  ok: true; scanned: number; found: number;
  window: { from: string; to: string };
  ran: string[]; skipped: SweepSource[]; note: string;
}
export interface Signal {
  id: string; source: string; occurred_at: string | null;
  from_name: string | null; from_handle: string | null;
  subject: string | null; excerpt: string | null; permalink: string | null;
  score: number | null; reasoning: string | null; intent: string | null;
  company_id: string | null; matched_domain: string | null; status: string;
}

/** Which collectors are wired, and when collection last ran. */
export const sweepStatus = () => request<SweepStatus>('/api/sweep');

/** Collect now, over a window. Same path the daily agent uses. */
export const runSweep = (body: { days?: number; from?: string; to?: string; sources?: string[] }) =>
  request<SweepResult>('/api/sweep', { method: 'POST', body: JSON.stringify(body) });

export interface DomainSuggestion {
  id: string; name: string; slug: string;
  domain: string | null; confidence: 'high'|'medium'|'low'; reasoning: string;
}
export interface DomainResult {
  ok: true; checked: number; suggestions: DomainSuggestion[]; note: string;
}

/** Suggest websites for accounts missing one. Writes suggestions, never the field. */
export const findDomains = (slug?: string) =>
  request<DomainResult>(`/api/domain${slug ? `/${slug}` : ''}`, { method: 'POST' });

export interface CadenceResult {
  ok: true; rationale: string; touches: number; dropped: string[]; note: string;
}

/**
 * Propose a cadence and write it as draft touches. Nothing arms, nothing sends.
 *
 * A JOB, NOT A CALL. The recommender runs minutes, not seconds — it lives in
 * a background function and this helper hides the polling so both the account
 * record and the Today batch drive one implementation. Rejects with the job's
 * own error when the run fails.
 */
export async function recommendCadence(
  sequenceId: string,
  onTick?: (progress: string) => void,
): Promise<CadenceResult> {
  const started = await request<{ ok: true; job_id: string }>(
    `/api/cadence/${sequenceId}`, { method: 'POST' });
  for (let i = 0; i < 80; i++) {                       // ~4-minute ceiling
    await new Promise((r) => setTimeout(r, 3000));
    const { job } = await request<{ ok: true; job: AgentJob<Partial<CadenceResult>> }>(
      `/api/cadence/job/${started.job_id}`);
    if (job.progress) onTick?.(String(job.progress));
    if (job.status === 'done') {
      const o = job.output ?? {};
      return {
        ok: true,
        rationale: String(o.rationale ?? ''),
        touches: Number(o.touches ?? 0),
        dropped: Array.isArray(o.dropped) ? o.dropped : [],
        note: String(o.note ?? ''),
      };
    }
    if (job.status === 'failed' || job.status === 'cancelled') {
      throw new ApiError(500, String(job.error ?? 'The cadence run failed.'));
    }
  }
  throw new ApiError(504,
    'The cadence run is taking unusually long. It may still finish — check the account in a minute.');
}

/** Arm / pause / resume / cancel. Arming is a transition, not a field. */
export const sequenceAction = (
  id: string, action: 'arm' | 'pause' | 'resume' | 'cancel', body?: Record<string, unknown>,
) => request<{ ok: true; status: string; touches?: number; first_due?: string }>(
  `/api/sequence/${id}/${action}`,
  { method: 'POST', ...(body ? { body: JSON.stringify(body) } : {}) },
);

export interface RunResult {
  ok: true; staged?: number; auto_paused?: number; completed?: number;
  note?: string; dry_run?: boolean; would_stage?: number;
  touches?: { company: string; ref: string; due_on: string; to: string; subject: string }[];
}

/** Stage every touch that is due. Does NOT send — /api/send is the second step. */
export const runSequences = (dry = false) =>
  request<RunResult>(`/api/run${dry ? '?dry=1' : ''}`, { method: 'POST' });

export interface SendResult {
  ok: true; sent: number; failed: number; remaining: number;
  note?: string;
  results?: { id: string; ok: boolean; error?: string }[];
}

/** Send one queued message now. */
export const sendEmail = (id: string) =>
  request<SendResult>(`/api/send/${id}`, { method: 'POST' });

/** Drain the outbox, up to the identity's daily cap. */
export const drainOutbox = () => request<SendResult>('/api/send', { method: 'POST' });

export const getRecord = <T = Record<string, unknown>>(type: RecordType, id: string) =>
  request<{ ok: true; record: T }>(`/api/records/${type}/${id}`);

export const createRecord = <T = Record<string, unknown>>(
  type: RecordType, data: Record<string, unknown>,
) => request<{ ok: true; record: T }>(`/api/records/${type}`, {
  method: 'POST', body: JSON.stringify(data),
});

export const updateRecord = <T = Record<string, unknown>>(
  type: RecordType, id: string, patch: Record<string, unknown>,
) => request<{ ok: true; record: T }>(`/api/records/${type}/${id}`, {
  method: 'PATCH', body: JSON.stringify(patch),
});

export const deleteRecord = (type: RecordType, id: string) =>
  request<{ ok: true }>(`/api/records/${type}/${id}`, { method: 'DELETE' });

// Typed conveniences for the calls the UI makes constantly.
export const listDeals = (companyId?: string) =>
  listRecords<Deal>('deals', { company_id: companyId });
export const listTasks = (companyId?: string) =>
  listRecords<Task>('tasks', { company_id: companyId });
export const setTaskDone = (id: string, done: boolean) =>
  updateRecord<Task>('tasks', id, { done, done_at: done ? new Date().toISOString() : null });
export const listActivities = (filters: Filters = {}) =>
  listRecords<Activity>('activities', filters);
export const listEmails = (filters: Filters = {}) =>
  listRecords<EmailMessage>('emails', filters);

// --- types for the objects added alongside the record layer ------------------

export interface IcpProfile {
  id: string; name: string; is_default: boolean; summary: string | null;
  industries: string[]; sub_categories: string[];
  employee_min: number | null; employee_max: number | null;
  revenue_min: number | null; revenue_max: number | null;
  geos: string[]; funding_stages: string[]; ownership: string[]; motions: string[];
  technologies: string[]; exclusions: string[];
  weights: Record<string, number>; triggers: string[]; notes: string | null;
  cities: string[]; states: string[]; countries: string[]; pains: string | null;
  /** Real companies that look like the target. Examples, not exclusions. */
  seed_accounts: string[];
}

export interface Persona {
  id: string; icp_id: string | null; name: string; role_kind: string | null;
  titles: string[]; seniorities: string[]; departments: string[];
  goals: string | null; pains: string | null; triggers: string | null;
  objections: string | null; channels: string | null; message_angle: string | null;
  proof_points: string[];
}

export interface EmailMessage {
  id: string; company_id: string | null; contact_id: string | null;
  deal_id: string | null; touch_id: string | null;
  direction: 'outbound' | 'inbound';
  status: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed' | 'received' | 'bounced';
  from_email: string | null; from_name: string | null;
  to_email: string | null; to_name: string | null;
  subject: string | null; body: string | null; thread_key: string | null;
  scheduled_for: string | null; sent_at: string | null; received_at: string | null;
  opened_at: string | null; replied_at: string | null; error: string | null;
  created_at: string;
}

export interface NewsItem {
  id: string; company_id: string | null; discovered_id: string | null;
  title: string; url: string | null; source: string | null; summary: string | null;
  published_at: string | null; kind: string; is_trigger: boolean;
}

export interface DiscoveredAccount {
  id: string; icp_id: string | null; provider: string; provider_org_id: string | null;
  name: string; domain: string | null; url: string | null; industry: string | null;
  employees: number | null; hq: string | null; founded: number | null;
  description: string | null; icp_score: number | null; icp_reasons: string[];
  status: 'new' | 'added' | 'dismissed'; company_id: string | null;
  dismissed_reason: string | null; query: string | null; found_at: string;
}

export interface DealContact {
  id: string; deal_id: string; contact_id: string; role: string | null;
}

export const listMessages = (slug: string) =>
  request<{ ok: true; messages: Message[] }>(`/api/chat/${slug}`);

export const sendMessage = (slug: string, content: string) =>
  request<{ ok: true; messages: Message[] }>(`/api/chat/${slug}`, {
    method: 'POST',
    body: JSON.stringify({ content }),
  });

export interface QueueItem {
  id: string; kind: string; verb: string; object: string; why: string;
  href: string; minutes: number; weight: number;
  /** ICP fit of the account behind the item — the order inside a band. */
  fit?: number | null;
  batch?: {
    count: number; action: string; names: string[];
    refs?: { id: string; slug: string; name: string }[];
    cta?: string;
  };
}
export interface TodayResult {
  ok: true; items: QueueItem[]; minutes: number;
  later: number; later_items: QueueItem[]; headline: string;
}

/** The day's queue — ordered, capped, finishable. See lib/queue-core.ts. */
export const getToday = () => request<TodayResult>('/api/today');

export interface Hop {
  name: string; detail: string; kind: string; strength: number;
  contact_id?: string | null;
}
export interface Route {
  id: string; hops: Hop[]; cost: number;
  confidence: 'high' | 'medium' | 'low'; why: string; ask: string;
}
export interface PathsResult {
  ok: true; company: { id: string; slug: string; name: string };
  routes: Route[]; empty_reason: string | null; edges_total: number;
}

/** Ranked introduction routes into an account. */
export const getPaths = (slug: string) => request<PathsResult>(`/api/paths/${slug}`);

/** Infer edges from data already held. No provider call, no credits. */
export const harvestPaths = () =>
  request<{ ok: true; shared_capital: number; career_overlap: number;
            total: number; note: string }>('/api/paths/harvest', { method: 'POST' });

export interface IcpDraftResult {
  ok: true; icp_id: string; icp_name: string; personas: number;
  gaps: string; note: string;
}

/** Describe who you sell to; get a structured ICP and its committee. */
export const draftIcp = (text: string) =>
  request<IcpDraftResult>('/api/icp/draft', {
    method: 'POST', body: JSON.stringify({ text }),
  });

/**
 * A background run. `output` is per job kind, so it is a type parameter rather
 * than a union that grows every time a new agent is added — the union version
 * made unrelated job kinds structurally interchangeable, which is how a build
 * job typechecked as a research job.
 */
export interface AgentJob<O = ResearchOutput> {
  id: string; status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  progress: string | null; found: number; error: string | null;
  searches: number; in_tokens: number; out_tokens: number;
  created_at: string; finished_at: string | null;
  output: O;
}

export interface ResearchOutput {
  notes?: string; proposed?: number; written?: number;
  strategy_id?: string; company?: string; wedge?: string | null;
}

/** Start a research run. Returns immediately with a job id. */
export const startResearch = (body: { icp_id?: string; want?: number }) =>
  request<{ ok: true; job_id: string; status: string }>('/api/agent/research', {
    method: 'POST', body: JSON.stringify(body),
  });

export const getResearchJob = (id: string) =>
  request<{ ok: true; job: AgentJob }>(`/api/agent/research/${id}`);

export const listResearchJobs = () =>
  request<{ ok: true; jobs: AgentJob[] }>('/api/agent/research');

// ── Account strategy (see src/lib/strategy-core.ts) ─────────────────────────
// The agent proposes an angle; the operator applies it. Applying is what feeds
// `companies.wedge`, which is what the cadence recommender reads.

export interface StrategyEvidence { claim: string; source_url?: string }

export interface AccountStrategy {
  id: string;
  company_id: string;
  status: 'new' | 'applied' | 'dismissed';
  wedge: string | null;
  gap_statement: string | null;
  squeeze_note: string | null;
  hooks: string[];
  proof_points: string[];
  entry_persona: string | null;
  rationale: string | null;
  objections: string | null;
  evidence: StrategyEvidence[];
  unknowns: string | null;
  model: string | null;
  searches: number; in_tokens: number; out_tokens: number;
  applied_at: string | null;
  applied_fields: string[];
  created_at: string;
}

/** Start a strategy run on one account. Returns immediately with a job id. */
export const startStrategy = (slug: string) =>
  request<{ ok: true; job_id: string; status: string }>(
    `/api/strategy/run/${slug}`, { method: 'POST' });

export const getStrategyJob = (id: string) =>
  request<{ ok: true; job: AgentJob }>(`/api/strategy/job/${id}`);

export const listStrategies = (slug: string) =>
  request<{ ok: true; strategies: AccountStrategy[] }>(`/api/strategy/account/${slug}`);

/**
 * Copy a proposal onto the company. Fills blanks by default; `overwrite`
 * replaces what the operator already wrote. `skipped` names what it kept.
 */
export const applyStrategy = (id: string, overwrite = false) =>
  request<{ ok: true; written: string[]; skipped: string[]; note: string | null }>(
    `/api/strategy/${id}/apply`,
    { method: 'POST', body: JSON.stringify({ overwrite }) });

export const dismissStrategy = (id: string) =>
  request<{ ok: true; status: string }>(`/api/strategy/${id}/dismiss`, { method: 'POST' });

// ── The list builder (see src/lib/build-core.ts) ────────────────────────────
// Batches the WALK across accounts, never the SPEND. Reveal stays one person,
// one click, one credit.

export interface ContactSuggestion {
  id: string;
  company_id: string;
  company_name: string;
  company_slug: string;
  apollo_person_id: string | null;
  name: string;
  title: string | null;
  seniority: string | null;
  /** Which of the workspace's personas this title is, or null. Never guessed. */
  matched_persona: string | null;
  department: string | null;
  linkedin_url: string | null;
  status: 'new' | 'added' | 'dismissed';
  raw: { has_email?: boolean; departments?: string[] };
  found_at: string;
}

export interface BuildOutput {
  accounts?: number; enriched?: number; suggested?: number;
  credits?: number; skipped?: string[];
}
export type BuildJob = AgentJob<BuildOutput>;

export const startBuild = (slugs: string[], enrich = true) =>
  request<{ ok: true; job_id: string; status: string; accounts: number }>(
    '/api/build/run', { method: 'POST', body: JSON.stringify({ slugs, enrich }) });

export const getBuildJob = (id: string) =>
  request<{ ok: true; job: BuildJob }>(`/api/build/job/${id}`);

export const listSuggestions = (slug?: string) =>
  request<{ ok: true; suggestions: ContactSuggestion[] }>(
    `/api/build/suggestions${slug ? `?slug=${encodeURIComponent(slug)}` : ''}`);

export const markSuggestionAdded = (id: string, contactId: string | null) =>
  request<{ ok: true }>(`/api/build/suggestion/${id}/added`,
    { method: 'POST', body: JSON.stringify({ contact_id: contactId }) });

export const dismissSuggestion = (id: string) =>
  request<{ ok: true }>(`/api/build/suggestion/${id}/dismiss`, { method: 'POST' });


export interface Offering {
  id: string; name: string; kind: string; summary: string | null;
  problem: string | null; outcome: string | null;
  price_band: string | null; term: string | null;
  proof: string[]; best_for: string[]; is_active: boolean; sort_order: number;
}

export interface ReadinessStep {
  key: string; label: string; done: boolean; href: string; why: string;
}
export interface Readiness {
  onboarded: boolean;
  steps: ReadinessStep[];
  next: { label: string; href: string; why: string } | null;
  can_send: boolean;
}
export interface OnboardResult {
  ok: true; offerings: number; personas: number; icp_name: string;
  gaps: string; positioning: string; note: string;
  /** 'parsed' means it ran without an Anthropic key and is rougher. */
  source?: 'model' | 'parsed';
}

/** What is set up, what is missing, and the one next thing to do. */
export const getReadiness = () =>
  request<{ ok: true; readiness: Readiness; counts: Record<string, unknown> }>('/api/onboard');

/** Describe the business; draft offerings, ICP and personas in one pass. */
export const runOnboarding = (body: {
  text: string; org_name?: string; website?: string; operator_name?: string;
}) => request<OnboardResult>('/api/onboard', { method: 'POST', body: JSON.stringify(body) });

export interface OrgProfile {
  name: string | null; website: string | null;
  what_we_do: string | null; positioning: string | null;
  operator_name: string | null; operator_title: string | null;
  onboarded_at?: string | null;
}

/** Who is writing, and what they sell — read by every drafting agent. */
export const getProfile = () =>
  request<{ ok: true; profile: OrgProfile }>('/api/onboard');

export const saveProfile = (p: Partial<OrgProfile>) =>
  request<{ ok: true; profile: OrgProfile }>('/api/onboard',
    { method: 'PATCH', body: JSON.stringify(p) });

export interface IdentityTruth {
  id: string; from_email: string; believed: string;
  resend_domain: string | null; resend_status: string | null;
  truth: string; agrees: boolean;
}

/** What Resend says about your domains, versus what this workspace believes. */
export const checkSendingDomains = () =>
  request<{ ok: true; domains: { name: string; status: string }[];
            identities: IdentityTruth[]; disagreements: string[] }>(
    '/api/sending/domains');

/** Believe Resend. Promotes and demotes. */
export const syncSendingDomains = () =>
  request<{ ok: true; promoted: number; demoted: number;
            identities: IdentityTruth[]; note: string }>(
    '/api/sending/domains', { method: 'POST' });


// ── people ───────────────────────────────────────────────────────────────────

export interface Workspace {
  org_id: string; org_slug: string; org_name: string; role: string;
}
export interface Identity {
  user: {
    id: string; email: string; name: string | null;
    is_superadmin: boolean; status: string;
    /** Somebody else chose this password. The app blocks on it until changed. */
    must_change_password?: boolean;
  };
  workspaces: Workspace[];
  /** Role is 'superadmin' when a platform owner is visiting someone else's. */
  current: Workspace | null;
}
export interface TeamMember {
  id: string; email: string; name: string | null; role: string;
  status: string; is_superadmin: boolean; can_sign_in: boolean; is_you: boolean;
}

export const login = (email: string, password: string) =>
  request<Identity & { token: string; expires_at: string }>('/api/auth/login',
    { method: 'POST', body: JSON.stringify({ email, password }) });

export const signup = (b: { email: string; password: string; name?: string; company: string }) =>
  request<Identity & { token: string; expires_at: string; next?: string }>('/api/auth/signup',
    { method: 'POST', body: JSON.stringify(b) });

/**
 * Always resolves the same way for any address — see the handler. The UI must
 * NOT branch on the answer, or it rebuilds the enumeration oracle the server
 * refuses to be.
 */
export const forgotPassword = (email: string) =>
  request<{ ok: true; note: string }>('/api/auth/forgot',
    { method: 'POST', body: JSON.stringify({ email }) });

export const resetPassword = (token: string, password: string) =>
  request<Identity & { token: string; expires_at: string; note: string }>('/api/auth/reset',
    { method: 'POST', body: JSON.stringify({ token, password }) });

export interface Mailbox {
  id: string; provider: 'google' | 'microsoft'; email: string;
  display_name: string | null; is_default: boolean; status: string;
  last_error: string | null; connected_at: string; expires_at: string | null;
}

/** Never returns a token — see mailbox-core. */
export const listMailboxes = () =>
  request<{ ok: true; mailboxes: Mailbox[]; providers: { google: boolean; microsoft: boolean } }>(
    '/api/mailbox');

export const connectMailbox = (provider: 'google' | 'microsoft') =>
  request<{ ok: true; url: string }>('/api/mailbox/connect',
    { method: 'POST', body: JSON.stringify({ provider }) });

export const setDefaultMailbox = (id: string) =>
  request<{ ok: true }>(`/api/mailbox/${id}/default`, { method: 'POST' });

/** Sends to the connected address itself — nothing else can be mailed by this. */
export const testMailbox = (id: string) =>
  request<{ ok: true; sent_to: string }>(`/api/mailbox/${id}/test`, { method: 'POST' });

export const disconnectMailbox = (id: string) =>
  request<{ ok: true }>(`/api/mailbox/${id}`, { method: 'DELETE' });

export interface ScheduleResponse {
  ok: true;
  settings: Record<string, unknown>;
  total: number;
  sending: number;
  days: {
    date: string; sending: number; blocked: number;
    touches: {
      id: string; due_on: string; company: string; company_slug: string;
      contact: string | null; subject: string; channel: string; blocked: string | null;
    }[];
  }[];
  history: Record<string, unknown>[];
}

/** What is COMING — distinct from Today, which is what is queued. */
export const getSchedule = (days = 14) =>
  request<ScheduleResponse>(`/api/schedule?days=${days}`);

export const saveAutomation = (v: Record<string, unknown>) =>
  request<{ ok: true; settings: Record<string, unknown> }>('/api/schedule',
    { method: 'PATCH', body: JSON.stringify(v) });

export const logout = () =>
  request<{ ok: true }>('/api/auth/logout', { method: 'POST' });

export const whoAmI = () => request<{ ok: true } & Identity>('/api/auth/me');

export const switchWorkspace = (slug: string) =>
  request<{ ok: true } & Identity>('/api/auth/switch',
    { method: 'POST', body: JSON.stringify({ slug }) });

export const changePassword = (current: string, next: string) =>
  request<{ ok: true; token: string; note: string }>('/api/auth/password',
    { method: 'POST', body: JSON.stringify({ current, next }) });

export interface Funnel {
  sent: number; opened: number; clicked: number; replied: number; bounced: number;
  open_rate: number | null; click_rate: number | null;
  reply_rate: number | null; bounce_rate: number | null;
}
export interface OutboundReport {
  days: number;
  cold: Funnel;
  warm: Funnel;
  suppressed: { suppressed: number; unsubscribed: number; bounced: number; contacts: number };
  open_rate_caveat: string;
}

/** Counted by the database over the whole window — see reports-core.ts. */
export const getOutbound = (days = 30) =>
  request<{ ok: true } & OutboundReport>(`/api/reports/outbound?days=${days}`);

export interface FeedbackRow {
  id: string; kind: string; title: string; body: string | null;
  page: string | null; status: string;
  classification: string | null; priority: string | null;
  asana_task_url: string | null; created_at: string;
  reporter_name: string | null; reporter_email: string | null;
}

/** File a report from wherever you are standing. See feedback-core.ts. */
export const sendFeedback = (b: {
  kind: string; title: string; body?: string; page?: string; user_agent?: string;
}) => request<{ ok: true; id: string; note: string }>('/api/feedback',
  { method: 'POST', body: JSON.stringify(b) });

/** This workspace's reports, and what became of each. Never another's. */
export const listFeedback = () =>
  request<{ ok: true; feedback: FeedbackRow[] }>('/api/feedback');

export const listTeam = () =>
  request<{ ok: true; members: TeamMember[]; your_role: string | null; can_manage: boolean }>(
    '/api/team');

export const addTeamMember = (b: { email: string; name?: string; role: string; password?: string }) =>
  request<{ ok: true; created: boolean; note: string }>('/api/team',
    { method: 'POST', body: JSON.stringify(b) });

export const setTeamRole = (userId: string, role: string) =>
  request<{ ok: true }>(`/api/team/${userId}`,
    { method: 'PATCH', body: JSON.stringify({ role }) });

export const removeTeamMember = (userId: string) =>
  request<{ ok: true; note: string }>(`/api/team/${userId}`, { method: 'DELETE' });
